import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import AccessSessionProp from '../../../../commonComponents/propTypes/accessSessionProp';
import translate from '../../../../commonComponents/translate';
import Information from './components/session/Information';
import { compose, SESSION_TYPE } from '../../../../services/utilityFunctions';
import ClassroomInfo from '../../../sessions/classroom/components/ClassroomInfo';
import AcademySessionMap from '../../../sessions/detail/AcademySessionMap';
import SessionDocuments from '../../../sessions/classroom/components/SessionDocuments';
import SessionWebinarPlayer from '../../../../commonComponents/fileViewers/SessionWebinarPlayer';
import { checkIfUserIsInParticipantsList } from '../../../sessions/propTypes/sessionUtils';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import { useModal } from '../../../../services/customHooks';
import BiggerImageModal from '../../../../commonComponents/modal/BiggerImageModal';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import Notifications from '../../../../services/notifications';
import withCSRF from '../../../../commonComponents/withCSRF';
import GenericModal from '../../../../commonComponents/modal/GenericModal';
import SkipStepButton from './components/SkipStepButton';

const TABS = {
  INFORMATION: 'information',
  SESSION: 'session',
  DOCUMENTS: 'documents',
};

function JourneyContentSession({
  t,
  _csrf,
  session: sessionParam,
  languages,
  journeyId,
  journeySlug,
  sessionVerifiedParticipation,
  onSkipStep,
}) {
  const { loggedUser } = useContext(LoggedUserContext);
  const { _id: loggedUserId } = loggedUser;
  const [session, setSession] = useState(sessionParam);
  const { type, classroom } = session;

  const [tabActive, setTabActive] = useState(TABS.INFORMATION);
  const handleTabChange = useCallback(tab => setTabActive(tab), [setTabActive]);
  const [qrBiggerModal, showQRBiggerModal] = useModal({
    show: false,
    data: null,
  });
  const handleQRModal = link => {
    showQRBiggerModal({ show: true, data: link });
  };
  const isClassroomTab =
    tabActive === TABS.SESSION &&
    type === SESSION_TYPE.CLASSROOM &&
    classroom?.address;
  const isUserInParticipants = checkIfUserIsInParticipantsList(
    session,
    loggedUserId,
  );

  const getSession = () => {
    axios
      .get(`/api/access-session/${session._id}?journeyContext=${journeyId}`, {
        params: { populate: ['host', 'trainer'] },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      })
      .then(({ data: response }) => {
        setSession(response.data);
      })
      .catch(err => {
        console.log(err);
      });
  };
  useEffect(() => {
    getSession();
  }, [sessionParam, journeyId]);

  /** VERIFY PARTICIPATION */
  // Handle Modal from Participation
  const [verifyModal, showVerifyModal] = useModal({ show: false, data: null });
  const handleVerifyParticipation = () => {
    axios
      .put(
        `/api/access-session/${session._id}/confirm-participation`,
        {},
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      )
      .then(() => {
        showVerifyModal({
          show: true,
          data: {
            info: t('sessionDetails.verifyParticipation'),
            success: true,
          },
        });
      })
      .catch(err => {
        console.error('Err', err);
        showVerifyModal({
          show: true,
          data: { info: t(err.response.data.error), success: false },
        });
        Notifications.showErrorFromRequest(err, 'ERROR_UNKNOWN');
      });
  };

  useEffect(() => {
    if (sessionVerifiedParticipation) {
      handleVerifyParticipation();
    }
  }, []);

  return (
    <>
      {verifyModal.show && (
        <GenericModal
          onClose={() => {
            // redirect to the "main" page without /verify/participation
            window.location = `/journey/${journeySlug}`;
          }}
          title={verifyModal.data.success ? t('success') : t('error')}
          message={verifyModal.data.info}
          saveMessage={t('close')}
          modalId="verifyModal"
          titleClass={
            verifyModal.data.success ? 'success-title' : 'error-title'
          }
          containerClass="container-class"
        />
      )}
      {qrBiggerModal.show && (
        <BiggerImageModal
          size={256}
          value={qrBiggerModal.data}
          onClose={() => showQRBiggerModal({ show: false, data: null })}
        />
      )}
      <div id="sessionStepContent">
        <div className="m-portlet m-portlet--rounded m-portlet--rounded-force mainSection">
          <div className="m-portlet__head">
            <button
              type="button"
              onClick={() => handleTabChange(TABS.INFORMATION)}
              className={`tab-button ${
                tabActive === TABS.INFORMATION ? 'tab-button-active' : ''
              }`}
            >
              {t('information')}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange(TABS.SESSION)}
              className={`tab-button ${
                tabActive === TABS.SESSION ? 'tab-button-active' : ''
              }`}
            >
              {t(`sessions_${type}`)}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange(TABS.DOCUMENTS)}
              className={`tab-button ${
                tabActive === TABS.DOCUMENTS ? 'tab-button-active' : ''
              }`}
            >
              {t('access_session.detail.documents')}
            </button>
            {onSkipStep && (
              <div className="skip-wrapper">
                <SkipStepButton onClick={onSkipStep} />
              </div>
            )}
          </div>
          <div className="m-portlet__body">
            {tabActive === TABS.INFORMATION && (
              <Information
                session={session}
                languages={languages}
                journeyId={journeyId}
                onRefresh={() => getSession()}
              />
            )}
            {tabActive === TABS.SESSION && type === SESSION_TYPE.CLASSROOM && (
              <ClassroomInfo session={session} t={t} />
            )}
            {tabActive === TABS.SESSION && type === SESSION_TYPE.WEBINAR && (
              <SessionWebinarPlayer
                webinar={session.webinar}
                session={session}
                handleQRModal={handleQRModal}
                isUserInParticipants={isUserInParticipants}
                journeySlug={journeySlug}
              />
            )}
            {tabActive === TABS.DOCUMENTS && (
              <SessionDocuments session={session} />
            )}
          </div>
        </div>
        {isClassroomTab && (
          <div className="subSection">
            <AcademySessionMap t={t} session={session} />
          </div>
        )}
      </div>
    </>
  );
}

JourneyContentSession.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  journeyId: PropTypes.string.isRequired,
  journeySlug: PropTypes.string.isRequired,
  session: PropTypes.shape(AccessSessionProp.propType).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  sessionVerifiedParticipation: PropTypes.bool,
  onSkipStep: PropTypes.func,
};
JourneyContentSession.defaultProps = {
  sessionVerifiedParticipation: false,
  onSkipStep: null,
};

export default compose(translate, withCSRF)(JourneyContentSession);
